/**
 * A label that takes its colour from a ROLE rather than from a value.
 *
 * Webwin's `Label` has a `color` field, and setting it at construction would freeze the colour of
 * every caption in the app to whichever theme happened to be loaded first - the canvas equivalent of
 * the inline hex literal the OpenSilver views were forbidden from carrying. Resolving the role in
 * `onPaint`, where the theme is handed in, means a theme switch repaints correctly with nothing to
 * notify and no subscription to leak.
 *
 * `color` is a plain field on `Label`, so assigning it during paint costs nothing and cannot loop.
 */
import { Label, type LabelOptions, type RenderContext, type Size, type Theme } from 'webwin';
import { crm } from '../theme/CrmTheme.js';

export type TextRole =
  /** Body text. */
  | 'text'
  /** A secondary line: a subtitle, a supporting sentence. */
  | 'subtle'
  /** A caption, a unit, a field label - the quietest thing on the card. */
  | 'muted'
  /** A link, and the accent colour generally. */
  | 'brand'
  | 'danger'
  | 'warn'
  | 'ok'
  | 'info'
  /** Text on the dark navigation rail. */
  | 'sidebar'
  | 'sidebarMuted'
  /** Text on a brand-filled surface. */
  | 'onBrand';

export interface CrmLabelOptions extends LabelOptions {
  role?: TextRole;
}

export class CrmLabel extends Label {
  role: TextRole;

  constructor(options: CrmLabelOptions = {}) {
    super(options);
    this.role = options.role ?? 'text';
  }

  /**
   * A wrapping label that also sizes its own width (`wrap` + `autoWidth`) is as wide as its text when
   * the text fits on one line, and wraps at the width offered when it does not.
   *
   * Webwin's wrapping label always reports the whole width offered, and a non-wrapping one its
   * natural width whatever the room - so a name beside a chip either pushed the chip to the far edge
   * or ran past the card and was clipped. This is what lets a heading row hug its text on a desktop
   * and still wrap a long machine name on a phone instead of cutting it off.
   */
  override preferredSize(available: Size): Size {
    const fit = fitSize(this, available);
    if (fit) return fit;
    // A wrapping label given a fixed width (a name column) wraps at THAT width, and must say how tall
    // that makes it - measured at the row's width it reported one line and lost the rest.
    if (this.wrap && !this.autoWidth && this.hAlign !== 'stretch' && this.width > 0) {
      const size = super.preferredSize({ width: Math.min(available.width, this.width), height: available.height });
      return { width: this.width, height: size.height };
    }
    return super.preferredSize(available);
  }

  /**
   * A wrapping label in a wrap panel sizes its own width.
   *
   * A wrap panel reads the width a child already has unless the child measures itself, and a label is
   * born 100px wide - so a sentence among buttons ("Maintenance history - past 24 hours...") wrapped
   * into 100px and was clipped to the line's height. Limited to wrap panels, which offer each child
   * the whole line; a plain stack's offer is not reliable enough to hand a label its width.
   */
  override get autoWidth(): boolean {
    if (super.autoWidth) return true;
    const parent = this.parent as { lineSpacing?: number } | null;
    return this.wrap && typeof parent?.lineSpacing === 'number';
  }
  override set autoWidth(v: boolean) {
    super.autoWidth = v;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    switch (this.role) {
      case 'subtle':
        this.color = c.subtleText;
        break;
      case 'muted':
        this.color = c.mutedText;
        break;
      case 'brand':
        this.color = c.brand;
        break;
      case 'danger':
        this.color = c.danger;
        break;
      case 'warn':
        this.color = c.warn;
        break;
      case 'ok':
        this.color = c.ok;
        break;
      case 'info':
        this.color = c.info;
        break;
      case 'sidebar':
        this.color = c.sidebarText;
        break;
      case 'sidebarMuted':
        this.color = c.sidebarMuted;
        break;
      case 'onBrand':
        this.color = { r: 1, g: 1, b: 1, a: 1 };
        break;
      default:
        this.color = c.text;
        break;
    }
    super.onPaint(rc, theme);
  }
}

/**
 * The one-line size of a wrapping, self-width-sizing label whose text fits in `available`; null when
 * it does not fit (wrap at the width offered) or the label is not that kind. Shared by `CrmLabel`
 * and `CrmLink`, so a link in a sentence row wraps exactly as the text beside it does.
 */
export function fitSize(label: Label, available: Size): Size | null {
  const desktop = label.desktop;
  if (!label.wrap || !label.autoWidth || !desktop) return null;
  const font = label.font ?? desktop.theme.metrics.font;
  const natural = Math.ceil(desktop.rc.measureText(label.text, font).width);
  if (natural > available.width) return null;
  return { width: natural, height: label.lineHeight ?? desktop.rc.lineHeight(font) };
}
